import { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useDispatch, useI18n } from "Hooks";
import { getToddleDocAttachment } from "AppComponents/ToddleDoc/Modules/ToddleDocModule";

const ACTIVE_CONVERSATION_STATE = "ACTIVE";
const PERMISSION_FILTER_VIEW = { mode: "VIEW" };
const SUFFIX_WITH_COMMENTS = ".withComments";
const SUFFIX_WITHOUT_COMMENTS = ".withoutComments";

const getFileId = attachment =>
  _.get(attachment, "attachmentId", _.get(attachment, "id"));

const useToddleDocDownloadOptions = ({
  attachment,
  optionKeyPrefix = "toddleDocDownload",
  testIdPrefix = "toddleDoc-download",
}) => {
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const fileId = getFileId(attachment);

  const [openConversationCount, setOpenConversationCount] = useState(null);

  // Reset to the loading state whenever the underlying attachment changes so
  // the menu never reflects a previous doc's comment count while a fresh
  // fetch is in-flight (or before one starts).
  useEffect(() => {
    setOpenConversationCount(null);
  }, [fileId]);

  const fetchOpenConversationCount = useCallback(async () => {
    if (!fileId) return;
    // Snap back to the loading state at the start of each fetch as well, so a
    // re-open of the menu can't briefly show a stale enabled/disabled state.
    setOpenConversationCount(null);
    const node = await dispatch(
      getToddleDocAttachment({
        id: fileId,
        state: ACTIVE_CONVERSATION_STATE,
        filters: PERMISSION_FILTER_VIEW,
      })
    );
    setOpenConversationCount(_.get(node, "toddleDoc.conversations", []).length);
  }, [fileId, dispatch]);

  const handleMenuVisibleChange = useCallback(
    visible => {
      if (visible) fetchOpenConversationCount();
    },
    [fetchOpenConversationCount]
  );

  const withCommentsKey = `${optionKeyPrefix}${SUFFIX_WITH_COMMENTS}`;
  const withoutCommentsKey = `${optionKeyPrefix}${SUFFIX_WITHOUT_COMMENTS}`;

  const options = useMemo(() => {
    const isLoading = openConversationCount === null;
    const hasNoComments = openConversationCount === 0;
    return [
      {
        key: withCommentsKey,
        label: t("common:download_with_comments"),
        disabled: isLoading || hasNoComments,
        disabledTooltip: hasNoComments
          ? t("common:no_comments_to_include")
          : undefined,
        customTestId: `${testIdPrefix}-withComments-menuItem`,
      },
      {
        key: withoutCommentsKey,
        label: t("common:download_without_comments"),
        customTestId: `${testIdPrefix}-withoutComments-menuItem`,
      },
    ];
  }, [
    openConversationCount,
    t,
    testIdPrefix,
    withCommentsKey,
    withoutCommentsKey,
  ]);

  const isOwnOptionKey = useCallback(
    key => key === withCommentsKey || key === withoutCommentsKey,
    [withCommentsKey, withoutCommentsKey]
  );

  const getIncludeCommentsFromKey = useCallback(
    key => key === withCommentsKey,
    [withCommentsKey]
  );

  return {
    options,
    handleMenuVisibleChange,
    isOwnOptionKey,
    getIncludeCommentsFromKey,
    withCommentsKey,
    withoutCommentsKey,
  };
};

export default useToddleDocDownloadOptions;
